/**
 * Sheet Shell - per `Design/Mockups/_Notes - How to Play Sheet.md`.
 *
 * Two presentations from one component:
 *   - Mobile (`isMobile: true`): bottom sheet. Top corners 24px, bottom flush.
 *     Drag handle pill (36 x 4 px, `border` colour). 20px horizontal padding,
 *     8px top, 24px bottom.
 *   - Desktop (`isMobile: false`): 480px centred modal, 16px radius, 24px
 *     padding, 1px `border` stroke, `card` fill. No drag handle.
 *
 * Visual parity with the WORD5 mockup. Re-used for every game's
 * "How to Play" surface.
 *
 * PM 2026-06-10 bug pass: ESC routing + z-index ordering now flow through
 * the shared modal stack (see `./modal-stack.ts`).
 */

import { iconSvg } from './icons';
import { registerModal, type ModalStackEntry } from './modal-stack';

export interface SheetShellProps {
  title: string;
  /** Body content (caller-owned node). Rules block + example row, etc. */
  content: Node;
  onClose: () => void;
  isMobile: boolean;
}

export interface SheetHandle {
  update: (props: SheetShellProps) => void;
  destroy: () => void;
}

interface InternalRefs {
  backdrop: HTMLDivElement;
  shell: HTMLDivElement;
  dragHandle: HTMLDivElement;
  title: HTMLHeadingElement;
  closeBtn: HTMLButtonElement;
  body: HTMLDivElement;
}

function buildSkeleton(): InternalRefs {
  const backdrop = document.createElement('div');
  backdrop.className = 'gv-sheet-backdrop';
  backdrop.setAttribute('role', 'dialog');
  backdrop.setAttribute('aria-modal', 'true');

  const shell = document.createElement('div');
  shell.className = 'gv-sheet-shell';
  backdrop.appendChild(shell);

  const dragHandle = document.createElement('div');
  dragHandle.className = 'gv-sheet-drag-handle';
  dragHandle.setAttribute('aria-hidden', 'true');
  shell.appendChild(dragHandle);

  const header = document.createElement('div');
  header.className = 'gv-sheet-header';

  const title = document.createElement('h2');
  title.className = 'gv-sheet-title';
  header.appendChild(title);

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'gv-modal-close';
  closeBtn.setAttribute('aria-label', 'Close');
  closeBtn.appendChild(iconSvg('x', 18));
  header.appendChild(closeBtn);

  shell.appendChild(header);

  const body = document.createElement('div');
  body.className = 'gv-sheet-content';
  shell.appendChild(body);

  return { backdrop, shell, dragHandle, title, closeBtn, body };
}

export function mountSheetShell(target: HTMLElement, props: SheetShellProps): SheetHandle {
  const refs = buildSkeleton();
  let current = props;

  function onBackdropClick(e: MouseEvent): void {
    if (e.target === refs.backdrop) current.onClose();
  }
  function onCloseClick(): void {
    current.onClose();
  }

  refs.backdrop.addEventListener('click', onBackdropClick);
  refs.closeBtn.addEventListener('click', onCloseClick);

  function render(p: SheetShellProps): void {
    current = p;
    const mode = p.isMobile ? 'mobile' : 'desktop';
    refs.backdrop.setAttribute('data-mode', mode);
    refs.shell.setAttribute('data-mode', mode);
    refs.dragHandle.hidden = !p.isMobile;

    refs.title.textContent = p.title;
    refs.title.id = `gv-sheet-${slug(p.title)}`;
    refs.backdrop.setAttribute('aria-labelledby', refs.title.id);

    refs.body.replaceChildren(p.content);
  }

  render(props);
  target.appendChild(refs.backdrop);

  const entry: ModalStackEntry = registerModal({
    element: refs.backdrop,
    tier: 'sheet',
    onClose: (): void => current.onClose(),
    dismissOnEsc: true,
    dismissOnBackdrop: true,
  });

  return {
    update: (next: SheetShellProps): void => {
      render(next);
    },
    destroy: (): void => {
      refs.backdrop.removeEventListener('click', onBackdropClick);
      refs.closeBtn.removeEventListener('click', onCloseClick);
      entry.release();
      refs.backdrop.remove();
    },
  };
}

function slug(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}
